// 戰鬥效果處理器
// Combat Effect Handlers

import type { GameState } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('CombatHandlers');

/**
 * 戰鬥成功效果處理器
 */
export class CombatSuccessHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('戰鬥成功') || text.includes('偷襲成功');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「偷襲成功時，使你墓地X被召喚」
    const sneakSummonMatch = effectText.match(/偷襲成功時[，,]使你墓地(\d*)([^被]+)?被召喚/);
    if (sneakSummonMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onSneakSuccess',
        value: 'summonFromGraveyard',
        targetSelector: {
          zone: 'graveyard',
          side: 'self',
          filter: sneakSummonMatch[2] ? { name: sneakSummonMatch[2].trim() } : undefined,
          count: sneakSummonMatch[1] ? parseInt(sneakSummonMatch[1], 10) : 1,
        },
      });
    }

    // 解析「戰鬥成功時，可展示...」
    const combatRevealMatch = effectText.match(/戰鬥成功時[，,]可?展示/);
    if (combatRevealMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onCombatSuccess',
        value: 'reveal',
      });
    }

    // 解析「你的其他單位偷襲成功時」
    if (effectText.includes('你的其他單位偷襲成功時')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onOtherSneakSuccess',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    // 這些效果需要在戰鬥階段觸發
    return state;
  }
}

/**
 * 戰鬥破壞效果處理器
 */
export class CombatDestroyHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被戰鬥破壞') || text.includes('戰鬥時不會被破壞');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「此卡在戰鬥時不會被破壞」
    if (effectText.includes('戰鬥時不會被破壞') || effectText.includes('此單位戰鬥時不會被破壞')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'indestructibleInCombat',
      });
    }

    // 解析「被戰鬥破壞時，使敵方獎勵+X」
    const destroyRewardMatch = effectText.match(/被戰鬥破壞時[，,]使敵方獎勵\+(\d+)/);
    if (destroyRewardMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onCombatDestroy',
        value: parseInt(destroyRewardMatch[1], 10),
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
        },
      });
    }

    // 解析「被戰鬥破壞時，使敵方抽X張牌」
    const destroyDrawMatch = effectText.match(/被戰鬥破壞時[，,]使敵方抽(\d+)張牌/);
    if (destroyDrawMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onCombatDestroyDraw',
        value: parseInt(destroyDrawMatch[1], 10),
      });
    }

    // 解析「被戰鬥破壞時，使敵方召喚X個小旅人」
    const destroySummonMatch = effectText.match(/被戰鬥破壞時[，,]使敵方召喚(\d+)個小旅人/);
    if (destroySummonMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onCombatDestroySummon',
        value: parseInt(destroySummonMatch[1], 10),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'indestructibleInCombat') {
      log.debug('Unit is indestructible in combat');
      return state;
    }

    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    return state;
  }
}

/**
 * 進攻/防守效果處理器
 */
export class AttackDefendHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('進攻') || text.includes('防守') || 
           text.includes('無法進攻') || text.includes('不得進攻');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「無法進攻」
    if (effectText.includes('無法進攻')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'cannotAttack',
      });
    }

    // 解析「不得進攻此單位」
    if (effectText.includes('不得進攻此單位')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'cannotBeAttacked',
      });
    }

    // 解析「進攻時+X攻擊力」
    const attackBuffMatch = effectText.match(/進攻時\+(\d+)攻擊力/);
    if (attackBuffMatch) {
      actions.push({
        type: 'buff',
        sourceCardId: '',
        customHandler: 'attackBuff',
        value: parseInt(attackBuffMatch[1], 10),
      });
    }

    // 解析「進攻的此單位將進攻敵方場上所有非橫放單位位置」
    if (effectText.includes('進攻敵方場上所有')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'attackAll',
      });
    }

    // 解析「使敵方場上X進攻單位被轉正」
    const untapAttackerMatch = effectText.match(/使敵方場上(\d*)個?([^進]+)?進攻單位被轉正/);
    if (untapAttackerMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'untapAttacker',
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
          count: untapAttackerMatch[1] ? parseInt(untapAttackerMatch[1], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    log.debug(handler);
    return state;
  }
}

/**
 * 攻擊力加減效果處理器（增強版）
 */
export class AttackModifierHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('攻擊力') && (text.includes('+') || text.includes('-'))) ||
           text.includes('被賦予+') || text.includes('被賦予-');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你場上所有X +Y攻擊力」
    const allBuffMatch = effectText.match(/使(你|敵方)場上(所有)?([^+-]+)?([+-])(\d+)攻擊力/);
    if (allBuffMatch) {
      const isEnemy = allBuffMatch[1] === '敵方';
      const isPositive = allBuffMatch[4] === '+';
      const value = parseInt(allBuffMatch[5], 10);
      const filterName = allBuffMatch[3] ? allBuffMatch[3].trim() : undefined;
      
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: isPositive ? value : -value,
        targetSelector: {
          zone: 'battlefield',
          side: isEnemy ? 'enemy' : 'self',
          filter: filterName ? { name: filterName } : undefined,
        },
      });
    }

    // 解析「被賦予+X攻擊力」（對自身）
    const selfBuffMatch = effectText.match(/被賦予([+-])(\d+)攻擊力/);
    if (selfBuffMatch && !allBuffMatch) {
      const isPositive = selfBuffMatch[1] === '+';
      const value = parseInt(selfBuffMatch[2], 10);
      
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: isPositive ? value : -value,
        customHandler: 'selfBuff',
      });
    }

    // 解析「使此單位正前方的敵方單位-X攻擊力」
    const frontDebuffMatch = effectText.match(/使此單位正前方的敵方單位([+-])(\d+)攻擊力/);
    if (frontDebuffMatch) {
      const isPositive = frontDebuffMatch[1] === '+';
      const value = parseInt(frontDebuffMatch[2], 10);
      
      actions.push({
        type: 'debuff',
        sourceCardId: '',
        value: isPositive ? value : -value,
        customHandler: 'debuffFront',
      });
    }

    // 解析「使此單位被賦予+X攻擊力。X=...」
    const dynamicBuffMatch = effectText.match(/使此單位被賦予\+X攻擊力[。.]?\s*X=([^\s。]+)/);
    if (dynamicBuffMatch) {
      actions.push({
        type: 'buff',
        sourceCardId: '',
        customHandler: 'dynamicBuff',
        value: dynamicBuffMatch[1],
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const buffValue = typeof action.value === 'number' ? action.value : 0;

    if (action.customHandler === 'selfBuff') {
      const sign = buffValue > 0 ? '+' : '';
      log.debug('Self buff: ' + sign + String(buffValue));
      return state;
    }

    if (action.customHandler === 'debuffFront') {
      log.debug('Debuff front unit: ' + String(buffValue));
      return state;
    }

    if (action.customHandler === 'dynamicBuff') {
      const value = action.value !== undefined ? String(action.value) : 'unknown';
      log.debug('Dynamic buff: X=' + value);
      return state;
    }

    // 一般 buff/debuff
    const selector = action.targetSelector;
    if (!selector) return state;

    const isPlayer1 = state.activePlayerId === state.player1.id;
    const isEnemy = selector.side === 'enemy';
    const sideKey = isEnemy ?
      (isPlayer1 ? 'player2Side' : 'player1Side') :
      (isPlayer1 ? 'player1Side' : 'player2Side');

    let newState = state;
    let modified = 0;

    const modifyLine = (lineKey: 'frontLine' | 'backLine'): void => {
      const side = newState.battlefield[sideKey];
      const line = side[lineKey].map((slot) => {
        if (slot.unit) {
          modified++;
          return {
            ...slot,
            unit: {
              ...slot.unit,
              atkModifier: (slot.unit.atkModifier ?? 0) + buffValue,
            },
          };
        }
        return slot;
      });

      newState = {
        ...newState,
        battlefield: {
          ...newState.battlefield,
          [sideKey]: {
            ...newState.battlefield[sideKey],
            [lineKey]: line,
          },
        },
      };
    };

    modifyLine('frontLine');
    modifyLine('backLine');

    const sign = buffValue > 0 ? '+' : '';
    log.debug('Modified ' + String(modified) + ' units: ' + sign + String(buffValue) + ' ATK');
    return newState;
  }
}

/**
 * 抗性效果處理器
 * Resistance keywords are granted as card keywords during summon.
 * The actual enforcement happens in spell/effect targeting logic.
 */
export class ResistanceHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('魔法抗性') || text.includes('效果抗性') || text.includes('破壞抗性');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('敵方魔法抗性')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantResistance',
        value: 'enemy_magic_resist',
      });
    } else if (effectText.includes('魔法抗性')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantResistance',
        value: 'magic_resist',
      });
    }

    if (effectText.includes('敵方效果抗性')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantResistance',
        value: 'enemy_effect_resist',
      });
    } else if (effectText.includes('效果抗性')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantResistance',
        value: 'effect_resist',
      });
    }

    if (effectText.includes('破壞抗性')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantResistance',
        value: 'destroy_resist',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const resistance = action.value !== undefined ? String(action.value) : 'unknown';
    log.debug('Resistance keyword assigned: ' + resistance);
    return state;
  }
}

/**
 * Check if a target unit resists a spell from casterOwnerId.
 */
export function resistsSpell(targetKeywords: string[] | undefined, targetOwnerId: string, casterOwnerId: string): boolean {
  if (!targetKeywords) return false;
  if (targetKeywords.includes('magic_resist')) return true;
  if (targetKeywords.includes('enemy_magic_resist') && targetOwnerId !== casterOwnerId) return true;
  return false;
}

/**
 * Check if a target unit resists a unit effect from sourceOwnerId.
 */
export function resistsUnitEffect(targetKeywords: string[] | undefined, targetOwnerId: string, sourceOwnerId: string): boolean {
  if (!targetKeywords) return false;
  if (targetKeywords.includes('effect_resist')) return true;
  if (targetKeywords.includes('enemy_effect_resist') && targetOwnerId !== sourceOwnerId) return true;
  return false;
}

/**
 * Check if a unit has destroy resistance.
 */
export function hasDestroyResistance(targetKeywords: string[] | undefined): boolean {
  if (!targetKeywords) return false;
  return targetKeywords.includes('destroy_resist');
}

/**
 * 無效化效果處理器
 */
export class NullifyHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('無效化');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('無效化該魔法卡')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'nullifySpell',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    log.debug(handler);
    return state;
  }
}

// 匯出所有戰鬥處理器
export const combatHandlers = {
  CombatSuccessHandler,
  CombatDestroyHandler,
  AttackDefendHandler,
  AttackModifierHandler,
  ResistanceHandler,
  NullifyHandler,
};
